import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { jwtDecode } from "jwt-decode";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { updateUser, setAuthLoading } from "../redux/slices/menu";
import RoomAPI from "../RoomAPI";
import { config } from "../config";

const premiumClaim = "https://franticfanfic.com/premium";
const connClaim = "https://franticfanfic.com/conn";
const screenName = "https://franticfanfic.com/screen_name";

export const useSyncAuthUser = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, getAccessTokenSilently, user, isLoading } =
    useAuth0();
  const selectUser = useAppSelector((state) => state.menu.user);

  useEffect(() => {
    const syncUser = async () => {
      if (isLoading) {
        dispatch(setAuthLoading(true));
        return; // Wait for auth0 to be ready
      }

      if (!isAuthenticated || !user?.sub) {
        // If not authenticated, and we have a user in redux, clear it.
        if (selectUser) {
          dispatch(updateUser(undefined));
        }
        dispatch(setAuthLoading(false)); // Auth resolved: no user
        return;
      }

      // If user is already synced, mark auth as complete
      if (selectUser?.auth0Id === user.sub) {
        dispatch(setAuthLoading(false));
        return;
      }

      try {
        dispatch(setAuthLoading(true)); // Start loading user data
        const accessToken = await getAccessTokenSilently({
          authorizationParams: { audience: config.auth0Audience },
        });

        const decoded: any = jwtDecode(accessToken);
        const { premium, conn, name } = {
          premium: decoded[premiumClaim],
          conn: decoded[connClaim],
          name: decoded[screenName],
        };

        const res = await RoomAPI.GetUser(accessToken);
        const isPremium = !!res.is_supporter || !!premium;

        dispatch(
          updateUser({
            user_id: res.user_id,
            name: name ?? user.name,
            premium: isPremium,
            rank: res.rank,
            auth0Id: user.sub,
            connection: conn,
          })
        );

        if (user.sub && !config.isDevelopment && isPremium) {
          import("logrocket").then((LogRocket) => {
            LogRocket.default.init("wkxouc/franticfanfic");
            LogRocket.default.identify(user.sub!, {
              name: name ?? user.name,
              premium: isPremium,
            });
          });
        }

        dispatch(setAuthLoading(false)); // Auth resolved: user loaded
      } catch (error) {
        console.error("Error syncing user data:", error);
        // Clear user on error as well, could be expired token etc.
        if (selectUser) {
          dispatch(updateUser(undefined));
        }
        dispatch(setAuthLoading(false)); // Auth resolved: error state
      }
    };

    syncUser();
  }, [
    dispatch,
    getAccessTokenSilently,
    isAuthenticated,
    selectUser,
    user,
    isLoading,
  ]);
};
